import {
    Select,
    SelectContent,
    SelectTrigger,
} from "@repo/ui/components/ui/select";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { cn } from "@repo/ui/lib/utils";

import {
    MobileNavBarButton,
    MobileNavBarTitle,
    MobileNavReturnToHome,
} from "@repo/ui/components/shared/MobileNavComponents";
import type { MenuOrNavOption as UIMenuOrNavOption } from "@repo/ui/components/shared/MobileNavBar";
import { HamburgerIcon } from "@repo/ui/components/shared/SMBIcons";
export type MenuOption = {
    title: string;
    show: boolean;
    external: boolean;
    key: string;
    children?: MenuOption[];
};

type MobileNavBarProps = {
    onSupportClick: () => void;
    onLoginClick?: () => void;
    setLanguage: (lang: string) => void;
    language?: string;
    aemData?: {
        primaryLogoImage: string;
        primaryLogoImageAltText: string;
        primaryButton: {
            label: string;
            displayIcon: boolean;
        };
        support?: {
            label: string;
            supportHeader: string;
            supportHeaderBody: string;
            cobrowseHeader: string;
            cobrowseText: string;
        };
        navs?: {
            language: string;
            mainMenu: string;
        };

    };
};

const MobileNavBar = (props: MobileNavBarProps) => {
    const {
        onSupportClick,
        onLoginClick,
        setLanguage,
        language,
        aemData
    } = props;
    const [selectOpen, setSelectOpen] = React.useState(false);

    // Track which menu item is focused for roving tabindex
    const [focusedIdx, setFocusedIdx] = React.useState(-1);


    // Stack of menu arrays for multi-level navigation
    const [navStack, setNavStack] = React.useState<MenuOption[][]>([]);
    // The menu items currently being displayed
    const [currentMenu, setCurrentMenu] = React.useState<MenuOption[]>([]);
    // Stack of menu titles for breadcrumb or header display
    const [menuTitleStack, setMenuTitleStack] = React.useState<string[]>([]);
    const menuRef = useRef<HTMLDivElement>(null);
    const navs = useMemo(
        () => [
            {
                title: aemData?.support?.label || "Support",
                show: true,
                external: false,
                key: "support",
            },
            {
                title: aemData?.navs?.language || "Language",
                show: true,
                external: false,
                key: "language",
                children: [
                    {
                        title: "United States - English",
                        href: "",
                        show: true,
                        external: false,
                        key: "en-us",
                    },
                    {
                        title: "Canada English",
                        href: "",
                        show: true,
                        external: false,
                        key: "en-ca",
                    },
                    {
                        title: "Canada Français",
                        href: "",
                        show: true,
                        external: false,
                        key: "fr-ca",
                    },

                ],
            },
        ],
        [aemData],
    );

    useEffect(() => {
        setNavStack([navs]);
        setCurrentMenu(navs);
        setMenuTitleStack([""]);
    }, [navs]);

    const onNavigateClick = useCallback(
        (
            e:
                | React.MouseEvent<HTMLButtonElement>
                | React.KeyboardEvent<HTMLButtonElement>,
            nav: MenuOption | undefined,
        ) => {

            e?.preventDefault();
            switch (nav?.key) {
                case "support":
                    onSupportClick();
                    break;
                case "login":
                    onLoginClick?.();
                    break;
                case "en-us":
                    setLanguage(nav?.key);
                    break;
                case "en-ca":
                    setLanguage(nav?.key);
                    break;
                case "fr-ca":
                    setLanguage(nav?.key);
                    break;

            }

            setSelectOpen(false); // Close dropdown after selection
        },
        [
            onSupportClick,
            onLoginClick,
            setLanguage,
        ],
    );


    const handleNavigation = useCallback((nav: MenuOption) => {
        if (nav?.children) {
            const children = nav.children;
            setNavStack((prev) => [...prev, children]);
            setCurrentMenu(children);
            setMenuTitleStack((prev) => [...prev, nav?.title]);
            // Focus the active language item (offset +1 for back button)
            const activeIdx = children.findIndex(child => child.key === language);
            setFocusedIdx(activeIdx >= 0 ? activeIdx + 1 : 1);
        }
    }, [language]);

    const handleGoBack = useCallback(() => {
        if (navStack.length > 1) {
            setNavStack((prev) => prev.slice(0, -1));
            setMenuTitleStack((prev) => prev.slice(0, -1));
            setCurrentMenu(navStack[navStack.length - 2] || []);
            setFocusedIdx(-1);
        }
    }, [navStack]);

    // Keyboard navigation: focus correct item when menu opens and trap tab/arrow keys
    const menuTitleStackKey = menuTitleStack.join("-");
    useEffect(() => {
        if (!selectOpen) return;
        if (focusedIdx < 0) return;
        // We use setTimeout(..., 0) to restore focus after SelectContent remounts due to a key change.
        // This ensures the DOM and refs are ready before focusing the menu item.
        const timeout = setTimeout(() => {
            const menuItems =
                menuRef.current?.querySelectorAll('[role="menuitem"]');
            if (menuItems && menuItems.length > 0) {
                let idx = focusedIdx;
                if (idx >= menuItems.length) idx = menuItems.length - 1;
                (menuItems[idx] as HTMLElement)?.focus();
            }
        }, 0);
        return () => clearTimeout(timeout);
    }, [currentMenu.length, menuTitleStackKey, selectOpen, focusedIdx,]);



    // Keyboard navigation handler for menu items
    const onMenuKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>, nav: MenuOption) => {
            const menuItems =
                menuRef.current?.querySelectorAll('[role="menuitem"]');

            if (!menuItems) return;
            const items = Array.from(menuItems) as HTMLElement[];
            const idx = items.findIndex((item) => item === e.currentTarget);
            if (e.key === "ArrowDown") {
                e.preventDefault();
                const nextIdx = (idx + 1) % items.length;
                setFocusedIdx(nextIdx);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                const prevIdx = (idx - 1 + items.length) % items.length;
                setFocusedIdx(prevIdx);
            } else if (e.key === "Tab") {
                e.preventDefault();
                const nextIdx = e.shiftKey
                    ? (idx - 1 + items.length) % items.length
                    : (idx + 1) % items.length;
                setFocusedIdx(nextIdx);
            } else if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                nav?.children
                    ? handleNavigation(nav)
                    : onNavigateClick(
                        e,
                        nav,
                    );
            } else if (e.key === "Escape") {
                setSelectOpen(false);
            } else if (e.key === "ArrowLeft" && navStack.length > 1) {
                handleGoBack();
            }
        },
        [handleNavigation, onNavigateClick, navStack.length, handleGoBack],
    );

    const onMenuOnClick = useCallback(
        (nav: MenuOption, e: React.MouseEvent<HTMLButtonElement>) => {
            nav?.children
                ? handleNavigation(nav)
                : onNavigateClick(
                    e,
                    nav,
                );
        },
        [handleNavigation, onNavigateClick],
    );

    const filteredNavigationList = currentMenu.filter(
        (nav: MenuOption) => {
            // Only show if user is NOT essential and show=true
            if (!nav?.show) return false;
            return true;
        },
    );
    return (
        <div
            className="lg:w-full flex justify-start  lg:hidden  "
            id="navigation-select"
            style={{ color: "#2E2E32" }}
        >
            <Select
                onOpenChange={(open) => {
                    setSelectOpen(open);
                    setFocusedIdx(-1);
                }}
                open={selectOpen}
                aria-label="Select navigation option"
            >
                <SelectTrigger
                    className={cn(
                        "navigation-mfe-trigger  w-fit border-0  lg:hidden",
                        "[data-state='open']:border-2! [data-state='open']:border-[#235AE4] [data-state='open']:[&>svg]:rotate-180",
                        "[data-state='close']:border-0!",
                    )}
                    id="navigation-select-trigger"
                    aria-label="Navigation menu"
                >
                    <HamburgerIcon className="h-8 w-8" />
                </SelectTrigger>
                <SelectContent
                    key={
                        `${currentMenu.length}-
                                    ${menuTitleStack.join("-")}-
                                    ${menuTitleStack[menuTitleStack.length - 1] || ""}`
                    }
                    className={cn(
                        "fixed! border-0 bg-white p-0 m-0 z-50 shadow-md",
                        "-left-[16px]!",
                        "top-[8px]! w-[68vw]! min-h-content! rounded-none!",
                    )}
                    id="navigation-select-content"
                    aria-label="Navigation options"
                    role="menu"
                    ref={menuRef}
                >

                    <div className="text-[#2E2E32]  menu-container will-change-[transform,height,contents]!" >
                        {navStack?.length > 1 && (
                            <>
                                <MobileNavReturnToHome
                                    onGoHome={handleGoBack}
                                    onMenuKeyDown={onMenuKeyDown as unknown as (e: React.KeyboardEvent<HTMLButtonElement>, nav: UIMenuOrNavOption) => void}
                                    tabIndex={focusedIdx === 0 ? 0 : -1}
                                    returnTitle={aemData?.navs?.mainMenu}
                                />
                                <MobileNavBarTitle
                                    title={
                                        menuTitleStack[
                                        menuTitleStack?.length - 1
                                        ]
                                    }
                                />
                            </>
                        )}

                        {filteredNavigationList?.map((nav, idx) => {
                            const itemKey = `${nav.title || ""}-${idx}`;
                            const expectedFocusIdx = navStack.length > 1 ? idx + 1 : idx;
                            const itemTabIndex = focusedIdx === expectedFocusIdx ? 0 : -1;

                            return (
                                <MobileNavBarButton
                                    nav={nav as unknown as UIMenuOrNavOption}
                                    active={navStack.length > 1 && nav.key === language}
                                    key={itemKey}
                                    isInsidePartner={false}
                                    INDUSTRY_PACKAGE_EXTERNAL_PARTNER={[]}
                                    onMenuOnClick={onMenuOnClick as unknown as (nav: UIMenuOrNavOption, e: React.MouseEvent<HTMLButtonElement>) => void}
                                    onMenuKeyDown={onMenuKeyDown as unknown as (e: React.KeyboardEvent<HTMLButtonElement>, nav: UIMenuOrNavOption) => void}
                                    pathname={""}
                                    idx={idx}
                                    currentMenu={currentMenu as unknown as UIMenuOrNavOption[]}
                                    tabIndex={itemTabIndex}
                                />
                            );
                        })}
                    </div>
                </SelectContent>
            </Select>
        </div>
    );
};

export default MobileNavBar;